import {
  BrainCoworkerEvidence,
  BrainPersonMembership,
} from '@this+that/models';

import type { BrainEntityView } from '../../../models/companyBrain';

export const BRAIN_PERSON_MEMBERSHIP_KEY: Record<
  BrainPersonMembership,
  string
> = {
  [BrainPersonMembership.SUGGESTED]: 'brain.people.suggested',
  [BrainPersonMembership.INVITED]: 'brain.people.invited',
  [BrainPersonMembership.MEMBER]: 'brain.people.teammate',
  [BrainPersonMembership.FORMER]: 'brain.people.formerMember',
};

export const BRAIN_PERSON_MEMBERSHIP_CLASS: Record<
  BrainPersonMembership,
  string
> = {
  [BrainPersonMembership.SUGGESTED]:
    'bg-semantic-bg-elevated text-semantic-text-secondary',
  [BrainPersonMembership.INVITED]:
    'bg-semantic-bg-warning-subtle text-semantic-text-warning',
  [BrainPersonMembership.MEMBER]:
    'bg-semantic-bg-info-subtle text-semantic-text-info',
  [BrainPersonMembership.FORMER]:
    'bg-semantic-bg-elevated text-semantic-text-tertiary',
};

export const BRAIN_COWORKER_EVIDENCE_KEY: Record<
  BrainCoworkerEvidence,
  string
> = {
  [BrainCoworkerEvidence.COMPANY_DOMAIN]:
    'brain.coworkers.evidence.companyDomain',
  [BrainCoworkerEvidence.SIGNATURE_TITLE]:
    'brain.coworkers.evidence.signatureTitle',
  [BrainCoworkerEvidence.BILATERAL_EXCHANGE]:
    'brain.coworkers.evidence.bilateralExchange',
  [BrainCoworkerEvidence.CC_WITH_INTERNALS]:
    'brain.coworkers.evidence.ccWithInternals',
};

const EVIDENCE_ORDER: readonly BrainCoworkerEvidence[] = [
  BrainCoworkerEvidence.COMPANY_DOMAIN,
  BrainCoworkerEvidence.SIGNATURE_TITLE,
  BrainCoworkerEvidence.BILATERAL_EXCHANGE,
  BrainCoworkerEvidence.CC_WITH_INTERNALS,
];

export interface BrainEvidenceChip {
  kind: BrainCoworkerEvidence;
  labelKey: string;
}

/** Known kinds only, once each, in a stable order; anything the server adds later is dropped rather than shown raw. */
export function toEvidenceChips(
  evidence: readonly BrainCoworkerEvidence[] | null | undefined,
): BrainEvidenceChip[] {
  const present = new Set(evidence ?? []);
  return EVIDENCE_ORDER.filter((kind) => present.has(kind)).map((kind) => ({
    kind,
    labelKey: BRAIN_COWORKER_EVIDENCE_KEY[kind],
  }));
}

export function resolvePersonMembership(
  entity: Pick<BrainEntityView, 'membership' | 'isTeammate' | 'isFormerMember'>,
): BrainPersonMembership | null {
  if (entity.membership) {
    return entity.membership;
  }
  if (entity.isTeammate) {
    return BrainPersonMembership.MEMBER;
  }
  return entity.isFormerMember ? BrainPersonMembership.FORMER : null;
}

export function formatConfidence(
  confidence: number | null | undefined,
): string | null {
  return typeof confidence === 'number' && Number.isFinite(confidence)
    ? confidence.toFixed(1)
    : null;
}
